import { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Vote as VoteIcon, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PollEmbed({ pollId }) {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [results, setResults] = useState(null);
  const [voted, setVoted] = useState(false);

  const fetchPoll = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/polls/${pollId}`);
      if (!r.ok) throw new Error();
      setPoll(await r.json());
      const rr = await fetch(`${API}/public/polls/${pollId}/results`);
      if (rr.ok) setResults(await rr.json());
    } catch { setPoll(null); }
    finally { setLoading(false); }
  }, [pollId]);

  useEffect(() => { fetchPoll(); }, [fetchPoll]);

  function toggle(oid) {
    if (!poll) return;
    if (poll.allow_multiple) {
      setSelected(prev => prev.includes(oid) ? prev.filter(x => x !== oid) : [...prev, oid]);
    } else {
      setSelected([oid]);
    }
  }

  async function vote() {
    if (selected.length === 0) return;
    setVoting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      try {
        const local = localStorage.getItem('emina_local_admin_token');
        const t = local || (isAuthenticated ? await getAccessTokenSilently() : null);
        if (t) headers.Authorization = `Bearer ${t}`;
      } catch {}
      const r = await fetch(`${API}/public/polls/${pollId}/vote`, {
        method: 'POST', headers, body: JSON.stringify({ option_ids: selected }),
      });
      if (r.status === 409) {
        setVoted(true);
        toast.message('Već ste glasovali');
        const rr = await fetch(`${API}/public/polls/${pollId}/results`);
        setResults(await rr.json());
        return;
      }
      if (!r.ok) throw new Error();
      const d = await r.json();
      setResults(d.results);
      setVoted(true);
      toast.success('Glas zabilježen');
    } catch { toast.error('Glasanje nije uspjelo'); }
    finally { setVoting(false); }
  }

  if (loading) return <div className="my-4 p-4 rounded-lg bg-gray-100 dark:bg-gray-800 text-center text-gray-400 text-sm"><Loader2 className="inline animate-spin mr-2" size={14} /> Učitavanje ankete…</div>;
  if (!poll) return null;

  const total = results?.total_votes || 0;
  const showResults = voted || !poll.is_active;

  return (
    <div className="my-6 p-5 rounded-xl bg-gradient-to-br from-red-50 to-amber-50 dark:from-red-950/30 dark:to-amber-950/30 border border-red-100 dark:border-red-900/30" data-testid={`poll-${pollId}`}>
      <div className="flex items-center gap-2 mb-3 text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-400">
        <VoteIcon size={14} /> Anketa
      </div>
      <div className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">{poll.question}</div>
      {!showResults ? (
        <div className="space-y-2">
          {poll.options.map(o => (
            <label key={o.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected.includes(o.id) ? 'bg-white dark:bg-gray-900 border-red-500' : 'bg-white/60 dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 hover:border-red-300'}`}
              data-testid={`poll-option-${o.id}`}>
              <input
                type={poll.allow_multiple ? 'checkbox' : 'radio'}
                checked={selected.includes(o.id)}
                onChange={() => toggle(o.id)}
                name={`poll-${pollId}`}
                className="accent-red-600"
              />
              <span className="text-sm text-gray-900 dark:text-gray-100">{o.label}</span>
            </label>
          ))}
          <button onClick={vote} disabled={voting || selected.length === 0 || !poll.is_active}
            className="mt-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg inline-flex items-center gap-2"
            data-testid={`poll-vote-btn-${pollId}`}>
            {voting ? <Loader2 size={14} className="animate-spin" /> : <VoteIcon size={14} />} Glasaj
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid={`poll-results-${pollId}`}>
          {poll.options.map(o => {
            const c = results?.counts?.[o.id] || 0;
            const pct = total ? Math.round((c / total) * 100) : 0;
            return (
              <div key={o.id}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{o.label}</span>
                  <span className="text-gray-500">{c} · {pct}%</span>
                </div>
                <div className="h-3 rounded-full bg-white dark:bg-gray-900 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-red-500 to-amber-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          <div className="text-xs text-gray-500 pt-2 flex items-center gap-1.5">
            {voted && <><CheckCircle2 size={12} className="text-green-600" /> Već ste glasovali. </>}
            Ukupno glasova: {total}
          </div>
        </div>
      )}
    </div>
  );
}
